import React, { useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import "./SportsBackgroundHero.css";

const SportsBackgroundHero = () => {
const isTouchDevice = useMemo(
  () =>
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0),
  []
);
  const particlesRef = useRef(null);
  const spotlightRef = useRef(null);
  const athleteWrapRef = useRef(null);
  const bgLayerRef = useRef(null);
  const cursorDotRef = useRef(null);
  const cursorRingRef = useRef(null);
  const animationFrameRef = useRef(null);

    useEffect(() => {
    if (isTouchDevice) {
      return;
    }
    const dot = cursorDotRef.current;
    const ring = cursorRingRef.current;
    const pc = particlesRef.current;
    const sc = spotlightRef.current;
    const aw = athleteWrapRef.current;
    const bg = bgLayerRef.current;

    if (!dot || !ring || !pc || !sc || !aw || !bg) {
      return;
    }

    const ctx = pc.getContext("2d");
    const sx = sc.getContext("2d");
    if (!ctx || !sx) {
      return;
    }

    document.body.classList.add("sports-hero-cursor");

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;

    const trail = [];

    const handleMouseMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
      dot.style.left = `${mx}px`;
      dot.style.top = `${my}px`;
      ring.style.left = `${mx}px`;
      ring.style.top = `${my}px`;

trail.push({ x: e.clientX, y: e.clientY, age: 0 });
if (trail.length > 80) trail.shift();
    };

    document.addEventListener("mousemove", handleMouseMove);

    const resize = () => {
      pc.width = window.innerWidth;
      pc.height = window.innerHeight;
      sc.width = window.innerWidth;
      sc.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    const pts = Array.from({ length: 120 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 2.2 + 0.2,
      vx: (Math.random() - 0.5) * 0.3,
      vy: -(Math.random() * 0.55 + 0.08),
      a: Math.random() * 0.5 + 0.05,
      white: Math.random() > 0.5,
    }));

    let cx = 0;
    let cy = 0;

    const loop = () => {
      ctx.clearRect(0, 0, pc.width, pc.height);

      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -4) {
          p.y = pc.height + 4;
          p.x = Math.random() * pc.width;
        }
        if (p.x < -4) p.x = pc.width + 4;
        if (p.x > pc.width + 4) p.x = -4;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.white
          ? `rgba(255,255,255,${p.a * 0.5})`
          : `rgba(146,37,37,${p.a})`;
        ctx.fill();
      });

trail.forEach((p) => {
  p.age += 1;
  const life = 55; // longer visible life
  const alpha = Math.max(0, 1 - p.age / life) * 0.66;
  if (alpha <= 0) return;

  ctx.beginPath();
  ctx.arc(
    p.x,
    p.y,
    Math.max(0, 3.1 * (1 - p.age / life)),
    0,
    Math.PI * 2
  );

  ctx.fillStyle = `rgba(230,95,95,${alpha})`;
  ctx.shadowColor = "rgba(220,80,80,0.45)";
  ctx.shadowBlur = 7;
  ctx.fill();
  ctx.shadowBlur = 0;
});

      sx.clearRect(0, 0, sc.width, sc.height);
      const g = sx.createRadialGradient(mx, my, 0, mx, my, 420);
      g.addColorStop(0, "rgba(146,37,37,0.1)");
      g.addColorStop(0.5, "rgba(100,20,20,0.04)");
      g.addColorStop(1, "transparent");
      sx.fillStyle = g;
      sx.fillRect(0, 0, sc.width, sc.height);

      const hw = window.innerWidth / 2;
      const hh = window.innerHeight / 2;
      cx += (((mx - hw) / hw) - cx) * 0.06;
      cy += (((my - hh) / hh) - cy) * 0.06;

      aw.style.transform = `translate(${cx * -18}px, ${cy * -10}px)`;
      bg.style.transform = `translate(${cx * 12}px, ${cy * 7}px) scale(1.04)`;

      animationFrameRef.current = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      document.body.classList.remove("sports-hero-cursor");
      document.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", resize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isTouchDevice]);

  return (
    <>
    {!isTouchDevice &&
      createPortal(
        <>
          <div id="c-dot" ref={cursorDotRef}></div>
          <div id="c-ring" ref={cursorRingRef}></div>
        </>,
        document.body
      )}

      <section className="sports-hero-root">
        <div id="scene">
          <div id="top-line"></div>
          <div id="bg-layer" ref={bgLayerRef}></div>
          <canvas
            id="spotlight-canvas"
            ref={spotlightRef}
            style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}
          ></canvas>

          <div id="beam-source"></div>
          <div id="light-beams">
            <div className="beam beam-4"></div>
            <div className="beam beam-2"></div>
            <div className="beam beam-1"></div>
            <div className="beam beam-3"></div>
            <div className="beam beam-5"></div>
          </div>
          <div id="beam-floor"></div>

          <div
            className="spd"
            style={{ top: "15%", "--w": "50%", "--dur": "2.6s", "--del": "0s" }}
          ></div>
          <div
            className="spd"
            style={{ top: "26%", "--w": "35%", "--dur": "3.4s", "--del": ".7s" }}
          ></div>
          <div
            className="spd"
            style={{ top: "40%", "--w": "60%", "--dur": "2.2s", "--del": "1.2s" }}
          ></div>
          <div
            className="spd"
            style={{ top: "55%", "--w": "42%", "--dur": "3s", "--del": "1.9s" }}
          ></div>
          <div
            className="spd"
            style={{ top: "68%", "--w": "55%", "--dur": "2.5s", "--del": ".4s" }}
          ></div>
          <div
            className="spd"
            style={{ top: "80%", "--w": "38%", "--dur": "3.8s", "--del": "1s" }}
          ></div>

          <div id="athlete-wrap" ref={athleteWrapRef}>
            <div id="aura"></div>
            <div id="ground-glow"></div>
           {/* <img id="athlete-img" src={athleteImg} alt="Aikyam Athlete" /> */}
            <div id="athlete-rim"></div>
          </div>

          <canvas id="particles" ref={particlesRef}></canvas>
        </div>
      </section>
    </>
  );
};

export default SportsBackgroundHero;